import React from 'react';
import { Avatar, Tag, Tooltip } from 'antd';
import { MailOutlined, UserOutlined } from '@ant-design/icons';
import moment from 'moment';
import type { DeliverableDocumentDto, DeliverableListItemDto, LovEffortLevelDto } from './types';

/**
 * The single document for a deliverable (exactly one in v1 — Decision 2).
 * @param {DeliverableListItemDto} deliverable - The deliverable row.
 * @returns {DeliverableDocumentDto} Its primary (only) linked document.
 */
export function getPrimaryDocument(deliverable: DeliverableListItemDto): DeliverableDocumentDto {
  return deliverable.documents[0];
}

/**
 * Whether a derived document format renders inline (HTML) vs downloads as a file (Decision 6).
 * @param {string} format - Derived document format (`html` | `pdf` | `docx` | `xlsx` | `pptx` | `other`).
 * @returns {boolean} True only for HTML (the inline-viewer format).
 */
export function isInlineFormat(format: string): boolean {
  return format === 'html';
}

/** Derived formats the shared Apryse WebViewer renders inside the viewer modal (Decision 6). */
export const APRYSE_FORMATS = new Set(['pdf', 'docx', 'xlsx', 'pptx']);

/** Which body the viewer modal renders for a document; `download` means it cannot be previewed. */
export type DeliverableViewerArm = 'html' | 'apryse' | 'eml' | 'msg' | 'download';

/**
 * Whether a file name names an email export.
 *
 * Tests the literal `.eml` **suffix** rather than the last dot-separated segment, so an
 * extensionless file named `eml` is not mistaken for one — `'eml'.split('.').pop()` is `'eml'`,
 * which is the false positive the deal-documents `resolveViewerKind` admits. Case-insensitive, and
 * anchored at the end so `report.eml.txt` is a `.txt`.
 * @param {string} fileName - The document's original file name.
 * @returns {boolean} True when the name ends in `.eml`.
 */
function isEmlFileName(fileName: string): boolean {
  return fileName.toLowerCase().endsWith('.eml');
}

/**
 * Whether a file name names an Outlook message export.
 *
 * Suffix-anchored for the same two reasons as {@link isEmlFileName} — an extensionless file named
 * `msg` is not one, and `report.msg.txt` is a `.txt` — plus a third that is specific to this pair:
 * the real specimen `41104-IDD-PaloAlto Networks.msg.eml` CONTAINS `.msg` but IS an `.eml`. Because
 * both tests are anchored at the end, the last extension wins and the two cannot fight, whichever
 * order they are consulted in.
 * @param {string} fileName - The document's original file name.
 * @returns {boolean} True when the name ends in `.msg`.
 */
function isMsgFileName(fileName: string): boolean {
  return fileName.toLowerCase().endsWith('.msg');
}

/**
 * The SINGLE arm-resolution function for a deliverable's document: which viewer body it takes.
 *
 * Everything that routes a deliverable derives from this — the panel's and the calendar row's
 * "Open" vs "Get"/inert-title decision (via {@link opensInModal}) and the viewer modal's own body
 * switch — so no two surfaces can disagree about what a file opens as. That drift is what once made
 * non-HTML deliverables download instead of open.
 *
 * The `.eml` arm sniffs the **file name**, never a backend `format` value: the backend derives no
 * limb for `.eml`, so such a document arrives as `format: 'other'` and stays that way. Format-driven
 * arms are resolved first, so this is purely additive — no document that routed somewhere before
 * routes differently now.
 *
 * The `.msg` arm follows that rule exactly and deliberately adds NO `format`-driven limb of its
 * own, even though the backend now does derive `format: 'msg'`. Routing stays name-driven
 * (OPERATIVE decision 5) and `format` remains a label only — which is why a `format: 'msg'` on an
 * `archive.zip` still downloads, and why `opensInModal('msg')` with no file name is `false`.
 *
 * The parameter is structural rather than a `Pick<DeliverableDocumentDto, …>` so surfaces holding no
 * DTO can still resolve an arm.
 * @param {object} doc - The document to route.
 * @param {string} doc.fileName - Original file name; the `.eml` sniff reads this.
 * @param {string | null} [doc.format] - Derived document format, when the surface has one.
 * @returns {DeliverableViewerArm} The arm the viewer modal renders for it.
 */
export function resolveDeliverableViewerArm(doc: { fileName: string; format?: string | null }): DeliverableViewerArm {
  const format = doc.format ?? '';
  if (isInlineFormat(format)) return 'html';
  if (APRYSE_FORMATS.has(format)) return 'apryse';
  if (isEmlFileName(doc.fileName)) return 'eml';
  if (isMsgFileName(doc.fileName)) return 'msg';
  return 'download';
}

/**
 * Whether a deliverable's document opens in the in-app viewer modal (vs. downloading).
 * A one-line derivation of {@link resolveDeliverableViewerArm}: every arm but `download` opens.
 * HTML renders inline (iframe); PDF/DOCX/XLSX/PPTX render via Apryse; an `.eml` or `.msg` renders
 * in the email viewer; everything else downloads.
 *
 * `format` stays the FIRST positional parameter and `fileName` is optional and second, because
 * callers that only know the format still call this with one argument. Omitting `fileName` means
 * the `.eml` arm cannot be reached — so a caller that has a file name must pass it.
 * @param {string} format - Derived document format (`html` | `pdf` | `docx` | `xlsx` | `pptx` | `other`).
 * @param {string} [fileName] - Original file name; required for an `.eml` to read as viewable.
 * @returns {boolean} True when the document renders inside the viewer modal.
 */
export function opensInModal(format: string, fileName = ''): boolean {
  return resolveDeliverableViewerArm({ fileName, format }) !== 'download';
}

/** Ant Design `Tag` color per derived document format; `undefined` → neutral tag. */
const FORMAT_TAG_COLOR: Record<string, string> = {
  html: 'blue',
  pdf: 'red',
  docx: 'geekblue',
  xlsx: 'green'
};

/**
 * The format chip shown per deliverable, derived from `document.format`
 * (`html` | `pdf` | `docx` | `xlsx` | `pptx` | `other`) — Decision 2. Separate signal
 * from the service type.
 * @param {object} props - Component props.
 * @param {string} props.format - Derived document format.
 * @returns {React.ReactElement} A colored format tag.
 */
export function FormatChip({ format }: { format: string }): React.ReactElement {
  const label = (format || 'file').toUpperCase();
  return (
    <Tag color={FORMAT_TAG_COLOR[format]} className="m-0 font-semibold tracking-wide">
      {label}
    </Tag>
  );
}

/**
 * Effort level name for a row, or `null` when unset or not in the catalog.
 * @param {LovEffortLevelDto[]} levels - The effort-level catalog.
 * @param {number | null} [effortLevelId] - `DeliverableListItemDto.effortLevelId`.
 * @returns {string | null} The level's `name`, or null.
 */
export function effortLevelName(levels: LovEffortLevelDto[], effortLevelId?: number | null): string | null {
  if (effortLevelId == null) return null;
  return levels.find((level) => level.id === effortLevelId)?.name ?? null;
}

/**
 * ISO timestamp → `MMM DD, YYYY` for the "Created" column; `—` when unparseable.
 * @param {string} createdAt - ISO 8601 timestamp from `deliverable.created_at`.
 * @returns {string} The formatted date, or an em-dash when unparseable.
 */
export function formatCreatedAt(createdAt: string): string {
  const m = moment(createdAt);
  return m.isValid() ? m.format('MMM DD, YYYY') : '—';
}

/**
 * Bytes → a compact human label (e.g. `1.4 MB`); `null` when size is unknown.
 * @param {number | null} [bytes] - File size in bytes, or null/undefined when unknown.
 * @returns {string | null} A compact size label, or null when no size is available.
 */
export function formatFileSize(bytes?: number | null): string | null {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unitIndex]}`;
}

/**
 * The "Notified" marker copy, from `DeliverableListItemDto.notifiedCount` (spec §7).
 *
 * The count is of **distinct people**, scoped by `deal_id` (D-5) — never of emails or of sends, so
 * re-notifying someone already notified does not move it. `0` is a real, loaded value rather than a
 * missing one, which is why zero reads "Not notified" rather than blank, `0`, or a spinner.
 * @param {number} count - `DeliverableListItemDto.notifiedCount`.
 * @returns {string} `N notified`, or `Not notified` at zero.
 */
export function notifiedMarkerLabel(count: number): string {
  return count > 0 ? `${count} notified` : 'Not notified';
}

/**
 * The list's coarse "Notified" marker (spec §7, D-5).
 *
 * Deliberately shows a count and nothing else — no notification history, no per-recipient delivery
 * status, no timestamps, no "who was notified" drill-down. Those are a separate backlogged project
 * and the marker is coarse **by design**.
 *
 * Rendered for **everyone who can see the list, including client users**: it is read-only
 * information about a deliverable they were sent, not an analyst affordance, so it is NOT behind
 * the notify ACL gate. Follows the "Created" cell's icon + text idiom.
 * @param {object} props - Component props.
 * @param {number} props.count - Distinct recipients notified on this deal.
 * @returns {React.ReactElement} The marker.
 */
export function NotifiedMarker({ count }: { count: number }): React.ReactElement {
  const notified = count > 0;
  return (
    <span className={`inline-flex items-center gap-1.5 ${notified ? 'text-gray-600' : 'text-gray-400'}`}>
      {/* Green matches the notify picker's recipient avatars, so the marker and the flow that
          produces it read as one feature. */}
      <MailOutlined className={notified ? 'text-[#0FBB20]' : 'text-gray-300'} />
      {notifiedMarkerLabel(count)}
    </span>
  );
}

/**
 * Created-by avatar + name (Decision 4). The name/initials come from the
 * `created_by` user relation and **can be null** — e.g. the Salesforce-imported
 * Crowdstrike FMV deliverable has `created_by = NULL`. A null author is by-design,
 * not a bug, so this renders a generic (initial-less) avatar and an em-dash name.
 * @param {object} props - Component props.
 * @param {string | null} [props.name] - Creator display name, or null/undefined.
 * @param {string | null} [props.initials] - Creator initials, or null/undefined.
 * @returns {React.ReactElement} Avatar + name row.
 */
export function CreatedByAvatar({
  name,
  initials
}: {
  name?: string | null;
  initials?: string | null;
}): React.ReactElement {
  const trimmedInitials = initials?.trim() || '';
  const trimmedName = name?.trim() || '';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Avatar size={24} className="bg-[#152D51] flex-none" icon={trimmedInitials ? undefined : <UserOutlined />}>
        {trimmedInitials || null}
      </Avatar>
      {trimmedName ? (
        <Tooltip title={trimmedName}>
          <span className="truncate text-gray-700">{trimmedName}</span>
        </Tooltip>
      ) : (
        <span className="text-gray-400" aria-label="Unknown analyst">
          —
        </span>
      )}
    </div>
  );
}
